'use client'

import {Image as AntImage} from 'antd'
import {getImageProps} from 'next/image'
import React, {useEffect, useRef, useState} from 'react'
import {FreeMode, Navigation, Thumbs} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import type {Swiper as SwiperClass} from 'swiper/types'

import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'

import {BodySelector, settings} from '@/src/config/settings'
import theme from '@/src/config/theme'

import {isMobile} from '@/src/shared/utils/platform'

import * as UI from './gallery.styled'

const Gallery = ({images, preview = true}: {images: string[]; preview?: boolean}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<Nullable<SwiperClass>>(null)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)
  const container = useRef<Nullable<HTMLElement>>(null)
  const mobile = isMobile()
  const sources = images.map(src => {
    if (/^https?:\/\//i.test(src)) return src
    if (src.startsWith('//')) return `https:${src}`
    return mobile ? new URL(src, settings.NEXT_PUBLIC_FRONTEND_URL).href : src
  })

  useEffect(() => {
    container.current = document.querySelector<HTMLElement>(BodySelector)
  }, [])

  return (
    <UI.Wrapper className="no-select">
      <UI.MainContainer>
        <AntImage.PreviewGroup
          items={sources}
          preview={{
            visible: preview && previewVisible,
            current: previewIndex,
            onVisibleChange: setPreviewVisible,
            onChange: setPreviewIndex,
            getContainer: container.current || false,
          }}>
          <UI.Swiper
            data-cy="product-gallery"
            navigation
            thumbs={{
              swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
            }}
            modules={[FreeMode, Navigation, Thumbs]}>
            {sources.map((src, index) => {
              const {props} = getImageProps({
                src,
                alt: '',
                width: 315,
                height: 315,
                sizes: '315px',
                loading: index === 0 ? 'eager' : 'lazy',
                fetchPriority: index === 0 ? 'high' : undefined,
                unoptimized: mobile,
              })
              return (
                <SwiperSlide key={src} data-cy="product-gallery-slide">
                  {/* Keep display srcSet out of Ant's original-resolution preview items. */}
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    {...props}
                    className="gallery-image"
                    style={{cursor: preview ? 'pointer' : undefined}}
                    onClick={
                      preview
                        ? () => {
                            setPreviewIndex(index)
                            setPreviewVisible(true)
                          }
                        : undefined
                    }
                  />
                </SwiperSlide>
              )
            })}
          </UI.Swiper>
        </AntImage.PreviewGroup>
      </UI.MainContainer>
      <UI.ThumbsContainer>
        <Swiper
          data-cy="product-gallery-thumbs"
          slidesPerView={3}
          breakpoints={{
            [theme.mediaSizes.md]: {
              slidesPerView: 6,
            },
          }}
          watchOverflow
          onSwiper={swiper => setThumbsSwiper(swiper)}
          modules={[FreeMode, Navigation, Thumbs]}
          className="thumbs">
          {sources.map((src, index) => (
            <SwiperSlide key={src} data-cy="product-gallery-slide">
              {/* Native img receives Next's rendition attributes without a second image loader. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                {...getImageProps({
                  src,
                  alt: '',
                  sizes: '95px',
                  width: 95,
                  height: 95,
                  loading: index === 0 ? 'eager' : 'lazy',
                  unoptimized: mobile,
                }).props}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </UI.ThumbsContainer>
    </UI.Wrapper>
  )
}

export default Gallery
